import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronDown, Lock, Menu, X } from 'lucide-react'
import { getRuntimeEnv } from '@/lib/runtimeEnv'
import { useBranding } from '@/lib/branding'
import { useFeedback } from '@/components/FeedbackProvider'
import { useFollowUps } from '@/components/FollowUpsProvider'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useNewCalloutModal } from '@/components/NewCalloutModalProvider'
import { useNewSessionModal } from '@/components/NewSessionModalProvider'

/**
 * A user-menu entry the current user may not open: still listed so staff know
 * the page exists, but inert, with a lock and a tooltip explaining why.
 */
function AdminOnlyMenuItem(props: { label: string }) {
  return (
    <span
      role="menuitem"
      aria-disabled="true"
      title="Only admins can access this"
      className="flex w-full cursor-not-allowed items-center justify-between rounded px-3 py-2 text-left text-gray-500"
    >
      {props.label}
      <Lock aria-hidden className="h-3.5 w-3.5" />
    </span>
  )
}

export interface HeaderUser {
  name: string
  avatarUrl?: string
  /** Shows the admin-only Settings entry in the user menu. */
  isAdmin?: boolean
}

export interface HeaderProps {
  user?: HeaderUser
  onLoginClick?: () => void
  onProfileClick?: () => void
  onLogoutClick?: () => void
  kioskMode?: boolean
}

function UnreadBadge(props: { count: number; tone?: 'alert' | 'subtle' }) {
  if (props.count <= 0) return null
  return (
    <span
      className={[
        'ml-1.5 inline-flex min-w-4 items-center justify-center rounded-full px-1 py-0.5 text-[10px] font-bold leading-none',
        // 'subtle' is a count, not an alarm — due follow-ups are routine work,
        // unlike unread feedback which genuinely wants attention.
        props.tone === 'subtle'
          ? 'bg-blue-100 text-blue-900'
          : 'bg-red-500 text-white',
      ].join(' ')}
    >
      {props.count}
    </span>
  )
}

export default function Header(props: HeaderProps) {
  const { user, onLoginClick, onProfileClick, onLogoutClick, kioskMode = false } = props
  const brand = useBranding()
  const { unreadCount } = useFeedback()
  const { dueCount } = useFollowUps()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { openNewCalloutModal } = useNewCalloutModal()
  const { openNewSessionModal } = useNewSessionModal()

  const showDevChrome =
    import.meta.env.DEV ||
    ['1', 'true', 'yes', 'on', 'dev'].includes(
      (getRuntimeEnv().VITE_SHOW_DEV_CHROME ?? '').trim().toLowerCase(),
    )
  const headerClass = 'bg-gray-800 text-white shadow-lg'
  const mobileMenuHoverClass = 'hover:bg-gray-700'
  const userMenuHoverClass = 'hover:bg-gray-600'
  const adminButtonClass =
    'rounded-full border border-gray-500/70 bg-transparent px-4 py-1.5 text-sm font-medium text-white hover:bg-gray-700'
  const mobileMenuBorderClass = 'border-gray-700'
  const mobileMenuActiveClass = 'bg-gray-700'
  const desktopNavLinkClass = 'rounded-md px-3 py-1 text-sm font-normal'
  const desktopNavActiveClass = 'bg-white text-blue-800'
  const mobileNavLinkClass = 'rounded-md px-3 py-2 text-sm font-normal'

  return (
    <>
      <header className={headerClass}>
        <div className="flex items-center justify-between p-4">
          <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
            {/* Mobile menu button */}
            <button
              type="button"
              className={`relative mr-2 rounded-md p-1.5 ${mobileMenuHoverClass} md:hidden`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
              {!isMobileMenuOpen && unreadCount + dueCount > 0 ? (
                <span className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full border border-gray-800 bg-red-500" />
              ) : null}
            </button>

            <h1 className="text-[clamp(0.8rem,3.8vw,1.25rem)] font-semibold uppercase leading-none">
              <Link to="/">
                {showDevChrome ? 'Development' : brand.name}
              </Link>
            </h1>
            {showDevChrome ? (
              <span className="inline-flex items-center rounded-full border border-blue-300/50 bg-blue-800 px-1.5 py-0.5 text-[clamp(8px,2vw,10px)] font-bold tracking-wide text-blue-100">
                DEV
              </span>
            ) : null}

            {/* Desktop nav links */}
            {!kioskMode ? (
              <nav className="hidden items-end space-x-1 ml-4 md:flex">
              <Link
                to="/bookings"
                className={desktopNavLinkClass}
                activeProps={{
                  className: desktopNavActiveClass,
                }}
              >
                Bookings
              </Link>

              <Link
                to="/services"
                className={desktopNavLinkClass}
                activeProps={{
                  className: desktopNavActiveClass,
                }}
              >
                Services
              </Link>

              <Link
                to="/customers"
                className={desktopNavLinkClass}
                activeProps={{
                  className: desktopNavActiveClass,
                }}
              >
                Customers
              </Link>

              <Link
                to="/follow-ups"
                className={desktopNavLinkClass}
                activeProps={{
                  className: desktopNavActiveClass,
                }}
              >
                Follow-ups
                <UnreadBadge count={dueCount} tone="subtle" />
              </Link>

              <Link
                to="/archived"
                className={desktopNavLinkClass}
                activeProps={{
                  className: desktopNavActiveClass,
                }}
              >
                Archived
              </Link>

              <Link
                to="/kiosk-control/check-in-out"
                className={desktopNavLinkClass}
                activeProps={{
                  className: desktopNavActiveClass,
                }}
              >
                Kiosk
              </Link>
              <Link
                to="/whatsapp"
                className={desktopNavLinkClass}
                activeProps={{
                  className: desktopNavActiveClass,
                }}
              >
                WhatsApp
              </Link>
              <Link
                to="/help"
                className={desktopNavLinkClass}
                activeProps={{
                  className: desktopNavActiveClass,
                }}
              >
                Help
              </Link>
              <Link
                to="/about"
                className={desktopNavLinkClass}
                activeProps={{
                  className: desktopNavActiveClass,
                }}
              >
                About
                <UnreadBadge count={unreadCount} />
              </Link>
              </nav>
            ) : null}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3">
            {user ? (
              <Popover
                open={isCreateMenuOpen}
                onOpenChange={setIsCreateMenuOpen}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="hidden items-center gap-1.5 rounded-full border border-blue-400 bg-blue-500 px-2.5 py-1 text-[clamp(11px,2.8vw,14px)] font-medium text-white hover:bg-blue-600 md:flex md:gap-2 md:px-4 md:py-1.5 md:text-sm"
                    aria-haspopup="menu"
                    aria-label="Create new"
                  >
                    <span>New</span>
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="z-50 w-52 border border-gray-700 bg-gray-900/95 p-1.5 text-sm text-slate-100 shadow-lg"
                >
                  <div className="space-y-0.5">
                    <button
                      type="button"
                      className="block w-full cursor-pointer rounded px-3 py-2 text-left hover:bg-gray-800"
                      onClick={() => {
                        setIsCreateMenuOpen(false)
                        openNewSessionModal(undefined, 'TeamViewer Session')
                      }}
                    >
                      TeamViewer Session
                    </button>
                    <button
                      type="button"
                      className="block w-full cursor-pointer rounded px-3 py-2 text-left hover:bg-gray-800"
                      onClick={() => {
                        setIsCreateMenuOpen(false)
                        openNewSessionModal(undefined, 'Sit-down Session')
                      }}
                    >
                      Sit-down Session
                    </button>
                    <button
                      type="button"
                      className="block w-full cursor-pointer rounded px-3 py-2 text-left hover:bg-gray-800"
                      onClick={() => {
                        setIsCreateMenuOpen(false)
                        openNewCalloutModal()
                      }}
                    >
                      Callout
                    </button>
                    <Link
                      to="/follow-ups"
                      search={{ new: true }}
                      className="block w-full cursor-pointer rounded px-3 py-2 text-left hover:bg-gray-800"
                      onClick={() => setIsCreateMenuOpen(false)}
                    >
                      Follow-up
                    </Link>
                  </div>
                </PopoverContent>
              </Popover>
            ) : null}
            {user ? (
              <Popover
                open={isUserMenuOpen}
                onOpenChange={setIsUserMenuOpen}
              >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`flex items-center gap-1.5 rounded-full pl-1 pr-2 py-1 text-[clamp(11px,2.8vw,14px)] sm:gap-2 sm:text-sm ${userMenuHoverClass}`}
                  aria-haspopup="menu"
                  aria-label="Open user menu"
                >
                  <div className="h-8 w-8 overflow-hidden rounded-full bg-blue-500 text-xs font-medium text-white">
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt={`${user.name} profile`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        {user.name
                          .split(' ')
                          .map((part) => part[0])
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>
                    )}
                  </div>
                  <span className="hidden max-w-[7rem] truncate text-sm font-medium text-white min-[390px]:inline">
                    {user.name}
                  </span>
                  <ChevronDown
                    className="h-4 w-4 text-gray-300"
                    aria-hidden="true"
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="z-50 w-48 border border-gray-700 bg-gray-900/95 p-1.5 text-sm text-slate-100 shadow-lg"
              >
                <div className="space-y-0.5">
                  <button
                    type="button"
                    className="block w-full cursor-pointer rounded px-3 py-2 text-left hover:bg-gray-800"
                    onClick={() => {
                      setIsUserMenuOpen(false)
                      if (onProfileClick) {
                        onProfileClick()
                      }
                    }}
                  >
                    Profile
                  </button>
                  {user.isAdmin ? (
                    <Link
                      to="/settings"
                      className="block w-full cursor-pointer rounded px-3 py-2 text-left hover:bg-gray-800"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Settings
                    </Link>
                  ) : (
                    <AdminOnlyMenuItem label="Settings" />
                  )}
                  {user.isAdmin ? (
                    <a
                      href="/_"
                      target="_blank"
                      rel="noreferrer"
                      className="block w-full cursor-pointer rounded px-3 py-2 text-left hover:bg-gray-800"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Admin
                    </a>
                  ) : (
                    <AdminOnlyMenuItem label="Admin" />
                  )}
                  <Link
                    to="/help"
                    className="block w-full cursor-pointer rounded px-3 py-2 text-left hover:bg-gray-800"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    Help
                  </Link>
                  <button
                    type="button"
                    className="block w-full cursor-pointer rounded px-3 py-2 text-left hover:bg-gray-800"
                    onClick={() => {
                      setIsUserMenuOpen(false)
                      if (onLogoutClick) {
                        onLogoutClick()
                      }
                    }}
                  >
                    Log out
                  </button>
                  <div className="border-t border-gray-800 mt-2 pt-3 pb-1 px-3 flex justify-center">
                    <img
                      src="/flagfinder-jobcard-light.svg"
                      alt="JobCard logo"
                      className="h-12"
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <>
              <a
                href="/_"
                target="_blank"
                rel="noreferrer"
                className={`${adminButtonClass} px-2.5 py-1 text-[clamp(11px,2.8vw,14px)] sm:px-4 sm:py-1.5 sm:text-sm`}
              >
                Admin
              </a>
              <button
                type="button"
                className="ml-2 rounded-full border border-blue-400 bg-blue-500 px-2.5 py-1 text-[clamp(11px,2.8vw,14px)] font-medium text-white hover:bg-blue-600 sm:px-4 sm:py-1.5 sm:text-sm"
                onClick={onLoginClick}
              >
                Log in
              </button>
            </>
            )}
          </div>
        </div>

        {/* Mobile menu drawer */}
        {isMobileMenuOpen && !kioskMode && (
          <nav className={`border-t ${mobileMenuBorderClass} px-4 py-3 md:hidden`}>
            <div className="flex flex-col space-y-1">
              <Link
                to="/bookings"
                className={mobileNavLinkClass}
                activeProps={{
                  className: mobileMenuActiveClass,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Bookings
              </Link>
              <Link
                to="/services"
                className={mobileNavLinkClass}
                activeProps={{
                  className: mobileMenuActiveClass,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Services
              </Link>
              <Link
                to="/customers"
                className={mobileNavLinkClass}
                activeProps={{
                  className: mobileMenuActiveClass,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Customers
              </Link>
              <Link
                to="/follow-ups"
                className={mobileNavLinkClass}
                activeProps={{
                  className: mobileMenuActiveClass,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Follow-ups
                <UnreadBadge count={dueCount} tone="subtle" />
              </Link>
              <Link
                to="/archived"
                className={mobileNavLinkClass}
                activeProps={{
                  className: mobileMenuActiveClass,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Archived
              </Link>
              <Link
                to="/kiosk-control/check-in-out"
                className={mobileNavLinkClass}
                activeProps={{
                  className: mobileMenuActiveClass,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Kiosk
              </Link>
              <Link
                to="/whatsapp"
                className={mobileNavLinkClass}
                activeProps={{
                  className: mobileMenuActiveClass,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                WhatsApp
              </Link>
              <Link
                to="/help"
                className={mobileNavLinkClass}
                activeProps={{
                  className: mobileMenuActiveClass,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Help
              </Link>
              <Link
                to="/about"
                className={mobileNavLinkClass}
                activeProps={{
                  className: mobileMenuActiveClass,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About
                <UnreadBadge count={unreadCount} />
              </Link>

              {user ? (
                <div className="mt-3 border-t border-gray-700/80 pt-3">
                  <div className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-300">
                    New
                  </div>
                  <div className="space-y-1 pl-3">
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-700"
                      onClick={() => {
                        setIsMobileMenuOpen(false)
                        openNewSessionModal(undefined, 'TeamViewer Session')
                      }}
                    >
                      + TeamViewer Session
                    </button>
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-700"
                      onClick={() => {
                        setIsMobileMenuOpen(false)
                        openNewSessionModal(undefined, 'Sit-down Session')
                      }}
                    >
                      + Sit-down Session
                    </button>
                    <button
                      type="button"
                      className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-700"
                      onClick={() => {
                        setIsMobileMenuOpen(false)
                        openNewCalloutModal()
                      }}
                    >
                      + Callout
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </nav>
        )}
      </header>
    </>
  )
}
